import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit, Trash2, RefreshCw, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { 
  getSizeCategories, 
  createSizeCategory, 
  updateSizeCategory, 
  deleteSizeCategory 
} from '../../../api/wholesale/sizeCategories';
import { 
  getTileTypes, 
  createTileType, 
  updateTileType, 
  deleteTileType 
} from '../../../api/wholesale/tileTypes';
import { getCompanies } from '../../../api/wholesale/companies';

const WholesaleCategoriesPage = () => {
  // Size Categories state
  const [sizeCategories, setSizeCategories] = useState([]);
  const [sizeCategoriesLoading, setSizeCategoriesLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Tile Types state
  const [tileTypesByCategory, setTileTypesByCategory] = useState({});
  
  // Companies for tile type company filter
  const [companies, setCompanies] = useState([]);
  
  // Forms state
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [newTileType, setNewTileType] = useState({ size_category_id: '', name: '', company_id: '' });
  const [editingTileType, setEditingTileType] = useState(null);
  
  // Loading states
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingTileType, setSavingTileType] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchSizeCategories();
    fetchCompanies();
  }, []);

  const fetchSizeCategories = async () => {
    setSizeCategoriesLoading(true);
    try {
      const data = await getSizeCategories(true);
      setSizeCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching size categories:', err);
      toast.error('Failed to load size categories');
    } finally {
      setSizeCategoriesLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await getCompanies();
      const data = response?.data || response || [];
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchTileTypes = async (sizeCategoryId) => {
    try {
      const data = await getTileTypes({ size_category_id: sizeCategoryId, include_inactive: true });
      setTileTypesByCategory(prev => ({
        ...prev,
        [sizeCategoryId]: Array.isArray(data) ? data : []
      }));
    } catch (err) {
      console.error('Error fetching tile types:', err);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const isExpanding = !prev[categoryId];
      if (isExpanding && !tileTypesByCategory[categoryId]) {
        fetchTileTypes(categoryId);
      }
      return { ...prev, [categoryId]: isExpanding };
    });
  };

  // Size Category CRUD
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    
    setSavingCategory(true);
    try {
      await createSizeCategory({ name: newCategoryName.trim() });
      toast.success('Size category created');
      setNewCategoryName('');
      fetchSizeCategories();
    } catch (err) {
      toast.error(err.message || 'Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!editingCategory?.name?.trim()) return;
    
    setSavingCategory(true);
    try {
      await updateSizeCategory(editingCategory.id, { name: editingCategory.name.trim() });
      toast.success('Size category updated');
      setEditingCategory(null);
      fetchSizeCategories();
    } catch (err) {
      toast.error(err.message || 'Failed to update category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this size category?')) return;
    
    try {
      await deleteSizeCategory(categoryId);
      toast.success('Size category deleted');
      fetchSizeCategories();
    } catch (err) {
      toast.error(err.message || 'Failed to delete category');
    }
  };

  // Tile Type CRUD
  const handleCreateTileType = async (e) => {
    e.preventDefault();
    if (!newTileType.size_category_id || !newTileType.name.trim()) return;
    
    setSavingTileType(true);
    try {
      await createTileType({
        size_category_id: parseInt(newTileType.size_category_id),
        name: newTileType.name.trim(),
        company_id: newTileType.company_id ? parseInt(newTileType.company_id) : null
      });
      toast.success('Tile type created');
      setNewTileType({ size_category_id: '', name: '', company_id: '' });
      fetchTileTypes(newTileType.size_category_id);
    } catch (err) {
      toast.error(err.message || 'Failed to create tile type');
    } finally {
      setSavingTileType(false);
    }
  };

  const handleUpdateTileType = async (e) => {
    e.preventDefault();
    if (!editingTileType?.name?.trim()) return;
    
    setSavingTileType(true);
    try {
      await updateTileType(editingTileType.id, { name: editingTileType.name.trim() });
      toast.success('Tile type updated');
      const sizeCategoryId = editingTileType.size_category_id;
      setEditingTileType(null);
      fetchTileTypes(sizeCategoryId);
    } catch (err) {
      toast.error(err.message || 'Failed to update tile type');
    } finally {
      setSavingTileType(false);
    }
  };

  const handleDeleteTileType = async (tileType) => {
    if (!confirm('Are you sure you want to delete this tile type?')) return;
    
    try {
      await deleteTileType(tileType.id);
      toast.success('Tile type deleted');
      fetchTileTypes(tileType.size_category_id);
    } catch (err) {
      toast.error(err.message || 'Failed to delete tile type');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Categories</h1>
          <p className="text-gray-500 mt-1">Manage size categories and tile types for products</p>
        </div>
        <button
          onClick={fetchSizeCategories}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 ${sizeCategoriesLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Size Categories */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Size Categories</h2>
              <p className="text-sm text-gray-500">Global categories for all companies</p>
            </div>
          </div>

          {/* Add Form */}
          <form onSubmit={handleCreateCategory} className="flex gap-2 mb-4">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g., 60x60"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <button
              type="submit"
              disabled={savingCategory || !newCategoryName.trim()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
            </button>
          </form>

          {/* Categories List */}
          {sizeCategoriesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
            </div>
          ) : sizeCategories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No size categories yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sizeCategories.map((category) => (
                <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div
                    className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center gap-2">
                      {expandedCategories[category.id] ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      {editingCategory?.id === category.id ? (
                        <form onSubmit={handleUpdateCategory} className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editingCategory.name}
                            onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                            autoFocus
                          />
                          <button type="submit" className="text-green-600 text-sm">Save</button>
                          <button type="button" onClick={() => setEditingCategory(null)} className="text-gray-500 text-sm">Cancel</button>
                        </form>
                      ) : (
                        <span className="font-medium text-gray-900">{category.name}</span>
                      )}
                    </div>
                    {!editingCategory && (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setEditingCategory({ id: category.id, name: category.name })}
                          className="p-1 text-gray-400 hover:text-blue-600"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Tile Types */}
                  {expandedCategories[category.id] && (
                    <div className="p-3 bg-white border-t border-gray-200">
                      {tileTypesByCategory[category.id]?.length > 0 ? (
                        <div className="space-y-2">
                          {tileTypesByCategory[category.id].map((type) => (
                            <div key={type.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              {editingTileType?.id === type.id ? (
                                <form onSubmit={handleUpdateTileType} className="flex gap-2 flex-1">
                                  <input
                                    type="text"
                                    value={editingTileType.name}
                                    onChange={(e) => setEditingTileType({ ...editingTileType, name: e.target.value })}
                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                    autoFocus
                                  />
                                  <button type="submit" className="text-green-600 text-sm">Save</button>
                                  <button type="button" onClick={() => setEditingTileType(null)} className="text-gray-500 text-sm">Cancel</button>
                                </form>
                              ) : (
                                <>
                                  <div>
                                    <span className="text-sm text-gray-900">{type.name}</span>
                                    {type.company_name && (
                                      <span className="text-xs text-gray-500 ml-2">({type.company_name})</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => setEditingTileType({ ...type })}
                                      className="p-1 text-gray-400 hover:text-blue-600"
                                    >
                                      <Edit className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTileType(type)}
                                      className="p-1 text-gray-400 hover:text-red-600"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">No tile types in this category</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Tile Type */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Add Tile Type</h2>
              <p className="text-sm text-gray-500">Create a new tile type under a size category</p>
            </div>
          </div>

          <form onSubmit={handleCreateTileType} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Size Category</label>
              <select
                value={newTileType.size_category_id}
                onChange={(e) => setNewTileType(prev => ({ ...prev, size_category_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select size category</option>
                {sizeCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tile Type Name</label>
              <input
                type="text"
                value={newTileType.name}
                onChange={(e) => setNewTileType(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Polished, Matte, Glazed"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company (Optional)
              </label>
              <select
                value={newTileType.company_id}
                onChange={(e) => setNewTileType(prev => ({ ...prev, company_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Global (All Companies)</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.company_name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">Leave empty to make this tile type available for all companies</p>
            </div>

            <button
              type="submit"
              disabled={savingTileType || !newTileType.size_category_id || !newTileType.name.trim()}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {savingTileType ? 'Creating...' : 'Create Tile Type'}
            </button>
          </form>

          {/* Info */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-gray-400 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Size Categories (e.g., 60x60) are global across all companies</li>
                  <li>Tile Types (e.g., Polished) can be global or company-specific</li>
                  <li>Products are assigned to a size category and optionally a tile type</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesaleCategoriesPage;
